import { useState } from 'react';

import { useGitBindings } from '../../hooks/use-git-bindings';
import { useGitCredentials } from '../../hooks/use-git-credentials';
import type { GitBindingResponse } from '../../lib/api-client';

const box: React.CSSProperties = {
  padding: '5px 10px',
  fontSize: 12,
  border: '1px solid var(--border-strong)',
  borderRadius: 6,
  background: 'var(--surface)',
  color: 'var(--text)',
  outline: 'none',
  fontFamily: 'inherit',
};

const primaryBtn: React.CSSProperties = {
  padding: '6px 12px',
  background: 'var(--accent)',
  color: '#fff',
  border: 0,
  borderRadius: 6,
  fontSize: 12,
  cursor: 'pointer',
  fontWeight: 500,
  fontFamily: 'inherit',
};

const ghostBtn: React.CSSProperties = {
  padding: '4px 9px',
  background: 'var(--surface)',
  color: 'var(--text)',
  border: '1px solid var(--border-strong)',
  borderRadius: 6,
  fontSize: 11,
  cursor: 'pointer',
  fontFamily: 'inherit',
};

/**
 * Per-tenant git bindings: which repo/branch this tenant materializes from and
 * which vaulted credential authenticates it. A tenant with no binding inherits
 * its nearest ancestor's binding (or the platform default) — see
 * docs/platform/30-git-integration.md.
 */
export function GitBindingsEditor({ tenantId, canManage }: { tenantId: string; canManage: boolean }) {
  const { bindings, status, error, createBinding, updateBinding, deleteBinding } = useGitBindings(tenantId);
  // The registry is owner-root scoped server-side, so the picker can only ever
  // offer a credential this caller is allowed to use.
  const { credentials } = useGitCredentials({ enabled: canManage });
  const [adding, setAdding] = useState(false);
  const [repoUrl, setRepoUrl] = useState('');
  const [branch, setBranch] = useState('main');
  const [credId, setCredId] = useState('');
  const [busyErr, setBusyErr] = useState<string | undefined>();

  /** Name the credential rather than echoing its id back at the operator. */
  const credentialLabel = (id: string | null) => {
    if (!id) return 'platform token';
    const found = credentials.find((c) => c.id === id);
    return found ? `credential “${found.name}”` : `credential ${id}`;
  };

  async function guard(fn: () => Promise<unknown>) {
    setBusyErr(undefined);
    try {
      await fn();
    } catch (err) {
      setBusyErr(err instanceof Error ? err.message : 'Failed');
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>Git binding</div>
        {status === 'loading' && <span style={{ fontSize: 11, color: 'var(--text-mute)' }}>loading…</span>}
        {canManage && !adding && (
          <button type="button" style={{ ...ghostBtn, marginLeft: 'auto' }} onClick={() => setAdding(true)}>
            + Add binding
          </button>
        )}
      </div>

      {status === 'error' && <div style={{ fontSize: 12, color: 'var(--kind-failure)' }}>{error}</div>}
      {busyErr && <div style={{ fontSize: 12, color: 'var(--kind-failure)' }}>{busyErr}</div>}

      {status === 'success' && bindings.length === 0 && !adding && (
        <div style={{ fontSize: 12, color: 'var(--text-mute)' }}>
          No binding — this tenant inherits its ancestor's repo (or the platform default).
        </div>
      )}

      {bindings.map((b: GitBindingResponse) => (
        <div
          key={b.id}
          style={{
            border: '1px solid var(--border)',
            borderRadius: 8,
            padding: 12,
            background: 'var(--surface)',
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <code style={{ fontSize: 12, color: 'var(--text)' }}>{b.repo_url}</code>
            <span style={{ fontSize: 11, color: 'var(--text-soft)' }}>@ {b.branch}</span>
            {b.is_primary && (
              <span style={{ fontSize: 10, color: 'var(--accent)', fontWeight: 500 }}>primary</span>
            )}
            <span
              style={{
                fontSize: 10,
                color: b.status === 'active' ? 'var(--text-soft)' : 'var(--kind-failure)',
                marginLeft: 'auto',
              }}
            >
              {b.status}
            </span>
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-soft)' }}>
            provider: {b.repo_provider} · {credentialLabel(b.git_credential_id)}
          </div>
          {canManage && (
            <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
              <button
                type="button"
                style={ghostBtn}
                onClick={() =>
                  guard(() => updateBinding(b.id, { status: b.status === 'active' ? 'disabled' : 'active' }))
                }
              >
                {b.status === 'active' ? 'Disable' : 'Enable'}
              </button>
              <button type="button" style={{ ...ghostBtn, color: 'var(--kind-failure)' }} onClick={() => guard(() => deleteBinding(b.id))}>
                Remove
              </button>
            </div>
          )}
        </div>
      ))}

      {adding && (
        <div
          style={{
            border: '1px dashed var(--border-strong)',
            borderRadius: 8,
            padding: 12,
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
          }}
        >
          <input placeholder="https://github.com/org/repo.git" value={repoUrl} onChange={(e) => setRepoUrl(e.target.value)} style={box} />
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <input placeholder="branch" value={branch} onChange={(e) => setBranch(e.target.value)} style={{ ...box, width: 140 }} />
            {/* Was a free-text box asking for a raw `crd_` id the operator had
                to go and find elsewhere. The registry knows its own names. */}
            <select
              data-testid="git-binding-credential"
              value={credId}
              onChange={(e) => setCredId(e.target.value)}
              style={{ ...box, flex: 1, minWidth: 200, cursor: 'pointer' }}
            >
              <option value="">platform token (no credential)</option>
              {credentials.map((c) => (
                <option key={c.id} value={c.id} disabled={c.status !== 'active'}>
                  {c.name}
                  {c.host_pattern ? ` — ${c.host_pattern}` : ''}
                  {c.status !== 'active' ? ` (${c.status.replace('_', ' ')})` : ''}
                </option>
              ))}
            </select>
          </div>
          {credentials.length === 0 && (
            <div style={{ fontSize: 10, color: 'var(--text-mute)' }}>
              No git credentials registered yet — add one under Git Credentials, or leave this on
              the platform token.
            </div>
          )}
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              type="button"
              style={primaryBtn}
              disabled={!repoUrl}
              onClick={() =>
                guard(async () => {
                  await createBinding({
                    repo_url: repoUrl,
                    branch: branch || 'main',
                    is_primary: true,
                    ...(credId ? { git_credential_id: credId } : {}),
                  });
                  setAdding(false);
                  setRepoUrl('');
                  setBranch('main');
                  setCredId('');
                })
              }
            >
              Save binding
            </button>
            <button type="button" style={ghostBtn} onClick={() => setAdding(false)}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
